import { useEffect } from 'react';
import { Toaster } from 'react-hot-toast';
import { Outlet, useRouteLoaderData } from 'react-router';
import { useSearchParams } from 'react-router-dom';

import { useSWEffect } from '@remix-pwa/sw';
import { ClientOnly } from 'remix-utils/client-only';

import { NectarAnalyticsProvider } from '~/context/NectarAnalyticsProvider';
import { PageProvider } from '~/context/PageProvider';
import useCreateVisitToken from '~/hooks/useLoaders/useCreateVisitToken';
import type { NectarAnalyticsSettings } from '~/hooks/useNectarAnalytics';
import useNotifications from '~/hooks/useNotifications';
import { usePushNotification } from '~/hooks/usePushNotification';
import useRemoveDraftPreviewCookie from '~/hooks/useRemoveDraftPreviewCookie';
import useRemoveParams from '~/hooks/useRemoveParams';
import useSetLastViewedResource from '~/hooks/useSetLastViewedResource';
import useJSDisabledSubmissionToasts from '~/hooks/useSubmissionErrorToast';
import useCookies from '~/hooks/useUtils/useCookies';
import usePWAReloadCheck from '~/hooks/useUtils/usePWAReloadCheck';
import useSiteLanguage from '~/hooks/useUtils/useSiteLanguage';
import useTypedLoaderData from '~/hooks/useUtils/useTypedLoaderData';
import useUserStatus from '~/hooks/useUtils/useUserStatus';
import type { RootLoaderData } from '~/root';
import type { PostLoaderData } from '~/routes/p/$slug';

import AdminBadge from '../AdminBadge';
import AnalyticsTracker from '../AnalyticsTracker';
import { getAppGlobals } from '../AppGlobals/utils';
import { BirdieLoader } from '../BirdieLoader';
import BeehiivHoney from '../CookieBanner';
import EnablePushNotificationPrompt from '../PWA/EnablePushNotificationPrompt';
import InstallPrompt from '../PWA/InstallPrompt';
import HumanEmbed from '../Scripts/HumanEmbed';

const appGlobals = getAppGlobals();

const App = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    site,
    isLoggedIn,
    subscription,
    admin,
    publication,
    currentPath,
    showLoggedInToast,
    isStagingPreview,
    SENTRY_RELEASE,
    isFetchingSubscription,
  } = useTypedLoaderData<RootLoaderData>();

  if (!site) {
    // show the refresh page message if we didn't get server data
    throw new Error('RefreshPageError');
  }

  useSWEffect();

  // [USER STATUS] Sets the user status based on the subscription tier and whether or not they are logged in
  const { isLoggedInOrSubscribed, isPremiumSubscription } = useUserStatus({
    isLoggedIn,
    subscriptionTier: subscription?.tier,
    subscriptionStatus: subscription?.status,
  });

  // [COOKIES] Sets the user-specific cookies
  // Note: Only set cookies client side when users are not in a session
  // The Set-cookie header server side will impact our ability to cache web pages for performance
  // *cookieSet* is a way of letting us know that app has mounted and we have retrieved all available cookies
  const { cookies, setCookie, cookiesSet, removeCookie } = useCookies({
    initialCookies: {
      popup_closed: isLoggedInOrSubscribed,
      email_capture_disabled: isLoggedInOrSubscribed,
      active_upgrade: isPremiumSubscription,
    },
  });
  useRemoveDraftPreviewCookie();

  // [LANGUAGE] Sets the language for the site based on the publication language
  useSiteLanguage({ language: publication?.language, setCookie });

  // [VISIT TOKEN] Creates a visit token for the user
  // We use visit tokens to track user site visits + page views and more
  // It is critical to many parts of our web analytics that this is set before signups and page views
  const visitTokenCookie = Boolean(cookies.visit_token);
  useCreateVisitToken({
    shouldCreateVisitToken: cookiesSet && !visitTokenCookie,
    setCookie,
  });

  useSetLastViewedResource({ setCookie, cookiesSet });

  // [REMOVE PARAMS] Removes query params from the url
  // We occasionally user query params to pass state between pages
  // To prevent params from impacting state more than once, clear them from the url
  useRemoveParams(['status', 'upgraded']);

  // [NOTIFICATIONS] Handles the notifications for the site
  useJSDisabledSubmissionToasts();
  useNotifications({ showLoggedInToast: isLoggedIn && showLoggedInToast });
  usePushNotification({ icon: publication?.logo?.thumb?.url });

  usePWAReloadCheck({ sentryRelease: SENTRY_RELEASE });

  // [NECTAR ANALYTICS] Get post ID if on a post page
  const postRouteData = useRouteLoaderData('routes/p/$slug') as PostLoaderData | undefined;
  const currentPostId = postRouteData?.post?.id;

  // Dispatch custom event after React hydration completes
  // This ensures embed support scripts wait for hydration before processing messages
  useEffect(() => {
    document.dispatchEvent(new CustomEvent('beehiiv:hydration_completed'));
  }, []);

  useEffect(() => {
    if (!isFetchingSubscription && (searchParams.get('jwt_token') || searchParams.get('admin_token'))) {
      searchParams.delete('jwt_token');
      searchParams.delete('admin_token');
      setSearchParams(searchParams, { preventScrollReset: true });
    }
  }, [searchParams, isFetchingSubscription, setSearchParams]);

  // Analytics configuration
  const analyticsSettings: NectarAnalyticsSettings = {
    batchSize: publication.nectar_analytics_settings.batch_size,
    batchTimeoutMs: publication.nectar_analytics_settings.batch_timeout_ms,
    pageViewTracking: publication.nectar_analytics_settings.page_view_tracking,
    clickTracking: publication.nectar_analytics_settings.click_tracking,
    ipTracking: publication.nectar_analytics_settings.ip_tracking,
  };

  const analyticsProps = {
    enabled: publication.nectar_analytics_enabled,
    nectarId: publication.nectar_id,
    publicationId: publication.id,
    postId: currentPostId,
    subscriptionId: subscription?.id,
    email: subscription?.email,
    settings: analyticsSettings,
  };

  return (
    <>
      <HumanEmbed />
      <NectarAnalyticsProvider analyticsProps={analyticsProps}>
        <PageProvider
          cookies={cookies}
          setCookie={setCookie}
          cookiesSet={cookiesSet}
          removeCookie={removeCookie}
          recaptchaSiteKey={appGlobals.CLOUDFLARE_TURNSTILE_SITE_KEY}
          visitToken={cookies.visit_token}
          referralId={cookies.referral_id}
          currentPath={currentPath}
          subscription={subscription}
          isStagingPreview={isStagingPreview}
        >
          <ClientOnly>{() => <BirdieLoader publication={publication} />}</ClientOnly>
          {admin && (
            <AdminBadge setCookie={setCookie} justLoggedIn={cookiesSet && !cookies.admin_logged_in && Boolean(admin)} />
          )}
          <Toaster position="top-center" />
          <AnalyticsTracker />
          <BeehiivHoney
            isCookieBannerEnabled={site?.viewable_site_version?.settings?.cookie_banner_enabled}
            cookies={cookies}
            setCookie={setCookie}
            privacyPolicyUrl={site?.viewable_site_version?.settings?.privacy_policy_url}
            termsOfUseUrl={site?.viewable_site_version?.settings?.terms_of_use_url}
          />
          <Outlet
            context={{
              publicationId: publication?.id,
              publicationBeehiivBranding: publication?.beehiiv_branding,
              publicationName: publication?.name,
              publicationCopyrightText: publication?.copyright_text,
              recaptchaSiteKey: appGlobals.CLOUDFLARE_TURNSTILE_SITE_KEY,
              cookies,
              setCookie,
              cookiesSet,
              removeCookie,
              visitToken: cookies.visit_token,
              referralId: cookies.referral_id,
              admin,
              isAdmin: Boolean(admin),
              isLoggedIn,
            }}
          />
          {publication?.web_template?.pwa_enabled && (
            <>
              <InstallPrompt />
              <EnablePushNotificationPrompt />
            </>
          )}
        </PageProvider>
      </NectarAnalyticsProvider>
    </>
  );
};

export default App;
